import type React from 'react';
import { hasStaticImage } from '../data/staticImages';

const IMAGE_OPTIMIZER_BASE = 'https://images.weserv.nl';
const STATIC_IMAGE_WIDTH = 960;
const REMOTE_IMAGE_WIDTH = 1280;
const DEFAULT_QUALITY = 80;

type ImageVariantSources = { avif?: string; webp?: string; fallback: string };

function isRemoteUrl(url?: string): boolean {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

function normalizeStaticPath(path: string): string {
  if (!path) return path;
  if (isRemoteUrl(path)) return path;
  const trimmed = path.replace(/^\.\//, '').replace(/\/+/g, '/');
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

function getMetaEnv(): Record<string, any> | undefined {
  if (typeof import.meta !== 'undefined') {
    return (import.meta as any).env as Record<string, any> | undefined;
  }
  return undefined;
}

function isDevEnv(): boolean {
  const env = getMetaEnv();
  if (!env) return false;
  return env.DEV === true || env.DEV === 'true' || env.MODE === 'development';
}

function isLocalhostUrl(url: string): boolean {
  return /https?:\/\/(localhost|127\.0\.0\.1)(?::\d+)?/i.test(url);
}

function getPublicAssetOrigin(): string | undefined {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }

  const env = getMetaEnv();
  const configured = env?.VITE_PUBLIC_SITE_URL || env?.PUBLIC_SITE_URL;
  if (configured) {
    return configured.replace(/\/$/, '');
  }

  return undefined;
}

function buildOptimizerUrl(source: string, format: 'webp' | 'avif', width: number): string {
  const params = new URLSearchParams({
    url: source,
    w: String(width),
    fit: 'cover',
    we: '1',
    q: String(DEFAULT_QUALITY),
    output: format,
  });
  return `${IMAGE_OPTIMIZER_BASE}/?${params.toString()}`;
}

function createRemoteSources(url: string): ImageVariantSources {
  if (/\.avif$/i.test(url)) {
    return { avif: url, fallback: url };
  }

  if (/\.webp$/i.test(url)) {
    return { webp: url, fallback: url };
  }

  if (/placehold\.co/i.test(url)) {
    return { fallback: url };
  }

  return {
    avif: buildOptimizerUrl(url, 'avif', REMOTE_IMAGE_WIDTH),
    webp: buildOptimizerUrl(url, 'webp', REMOTE_IMAGE_WIDTH),
    fallback: url,
  };
}

function createStaticSources(path: string): ImageVariantSources {
  const normalized = normalizeStaticPath(path);
  if (!hasStaticImage(normalized)) {
    return { fallback: normalized };
  }

  if (/\.avif$/i.test(normalized)) {
    return { avif: normalized, fallback: normalized };
  }

  if (/\.webp$/i.test(normalized)) {
    return { webp: normalized, fallback: normalized };
  }

  const origin = getPublicAssetOrigin();
  const shouldSkipOptimization = isDevEnv() || !origin;

  if (shouldSkipOptimization) {
    return { fallback: normalized };
  }

  const absoluteUrl = `${origin}${normalized}`;

  if (isLocalhostUrl(absoluteUrl)) {
    return { fallback: normalized };
  }

  return {
    avif: buildOptimizerUrl(absoluteUrl, 'avif', STATIC_IMAGE_WIDTH),
    webp: buildOptimizerUrl(absoluteUrl, 'webp', STATIC_IMAGE_WIDTH),
    fallback: normalized,
  };
}

export const PLACEHOLDER_LOGO = '/images/placeholder-logo.webp';
export const PLACEHOLDER_USER = '/images/placeholder-user.webp';

export function resolveImageUrl(
  url?: string,
  fallback: string = PLACEHOLDER_LOGO
): string {
  if (!url || url.trim() === '') return fallback;
  return url;
}

export function handleImgError(e: React.SyntheticEvent<HTMLImageElement, Event>, fallback: string = PLACEHOLDER_LOGO) {
  const target = e.currentTarget as HTMLImageElement;
  if (target.src !== fallback) {
    target.src = fallback;
  }
}

export function getImageSources(
  url?: string,
  fallback: string = PLACEHOLDER_LOGO
): ImageVariantSources {
  const resolved = resolveImageUrl(url, fallback);
  const normalized = normalizeStaticPath(resolved);

  if (isRemoteUrl(resolved)) {
    return createRemoteSources(resolved);
  }

  return createStaticSources(normalized);
}

/**
 * Determine the best image URL for an entity like a coffee shop or brand.
 * The priority order is:
 * 1. Provided remote URL (excluding placeholder images)
 * 2. Local image mapped by id or located under the provided directory
 * 3. Placeholder fallback
 */
export function resolveEntityImageUrl(
  id: string,
  imageUrl?: string,
  localMap: Record<string, string> = {},
  localDir = '/images/coffee-shops',
  fallback: string = PLACEHOLDER_LOGO
): string {
  const isRealRemote =
    !!imageUrl && /^https?:\/\//i.test(imageUrl) && !/placehold\.co/.test(imageUrl);
  if (isRealRemote) return imageUrl!;

  const mapped = localMap[id];
  if (mapped) return mapped;

  const candidate = `${localDir}/${id}.webp`;
  if (hasStaticImage(candidate)) return candidate;

  return fallback;
}

export function getEntityImageSources(
  id: string,
  imageUrl?: string,
  localMap: Record<string, string> = {},
  localDir = '/images/coffee-shops',
  fallback: string = PLACEHOLDER_LOGO
): { avif?: string; webp?: string; fallback: string } {
  const url = resolveEntityImageUrl(id, imageUrl, localMap, localDir, fallback);
  return getImageSources(url, fallback);
}
